import React, { useCallback, useState } from "react";
import { Formik, Form, useFormik } from "formik";
import * as Yup from "yup";
import { Button, Form as SUIForm, FormField as SUIFormField, Message, Table, Icon, Segment, Modal, ModalContent, MessageHeader, TableRow, TableBody, TableCell, ModalActions } from "@/components/bp/bp-ui";
import { profilesClient, queriesClient } from "@app/api2/Clients";
import { AlleleFormat, ParsedSampleDto, SampleCreateModel } from "@app/api2/api";
import { FileMultiUpload, IUploadedAnyFile } from "../../FileMultiUpload";
import { useKits } from "../../Hooks/UseKits";
import SemanticFormikSelect from "@common/Forms/SemanticFormikSelect";
import { useQuery } from "@tanstack/react-query";
import { toAlleleFormatName } from "@common/AlleleFormatNames";
import { useAlleleFormats } from "../../Hooks/useAlleleFormats";
import { useDialogContext } from "../../Hooks/DialogContext";
import { BPButton } from "@/components/bp/bpbutton";
import { TableHeader, TableHeaderCell } from "@/components/bp/bp-ui";

/**
 * Hook: useSampleParser
 * - Sends file to backend API to perform parsing.
 * - Returns parsed result, loading state, any error, and helper functions.
 */

export type ParsedSamples = {
    kitId: number;
    columns: string[];
    rows: ParsedSampleDto[];
    rawLines: string[];
    dataUrl?: string;
};

export function useSampleParser() {
    const [parsed, setParsed] = useState<ParsedSamples | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isUas, setIsUas] = useState(false);

    const reset = useCallback(() => {
        setParsed(null);
        setError(null);
        setLoading(false);
    }, []);

    const parseFile = useCallback(async (file: File, kitId: number, format?: AlleleFormat) => {
        setLoading(true);
        setError(null);
        setParsed(null);

        // helper to read file as Data URL
        const readAsDataUrl = (f: File): Promise<string> =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    if (typeof reader.result === "string") resolve(reader.result);
                    else reject(new Error("Failed to read file as data URL"));
                };
                reader.onerror = () => reject(new Error("Failed to read file"));
                reader.readAsDataURL(f);
            });

        try {
            const dataUrl = await readAsDataUrl(file);

            // profilesClient.parse is expected to accept an object with the data URL (and optional metadata)
            // adjust the payload shape if your Clients.ts defines a different contract.
            const payload = { sampleFile: dataUrl, fileName: file.name, kitId: kitId, format: format } as SampleCreateModel;

            // NOTE: ensure you import or reference profilesClient from Clients.ts in this module.
            const data = await profilesClient.parse(payload);

            setIsUas(data.isUAS ?? false);

            if (!data || !Array.isArray(data.newSample)) {
                throw new Error("Invalid response from parser API");
            }

            const normalized: ParsedSamples = {

                columns: [],
                rows: data.newSample,
                rawLines: [],
                kitId: payload.kitId ?? 0,
            };

            setParsed(normalized);
            setLoading(false);
            return normalized;
        } catch (err: any) {
            setError(err?.message ?? "Failed to parse file");
            setLoading(false);
            throw err;
        }
    }, []);

    return { parsed, loading, error, parseFile, reset, isUas };
}

/**
 * Component: SampleUploadForm
 * - Presents a file input for CSV/TSV file upload.
 * - Uses the parser hook to send the file to the API and shows a preview.
 * - Emits onParsed callback when parse succeeds.
 */

type Props = {
    onParsed?: (parsed: ParsedSamples) => void;
    submitLabel?: string;
    labId?: number | null;
}
const FileSchema = Yup.object().shape({
    kitId: Yup.number().min(1, "Please select a loci order").nullable().required("Loci order is required"),
    file: Yup.mixed().required("A file is required"),
});

export default function SampleUploadForm({ onParsed, submitLabel = "Upload & Parse" }: Props) {
    const [localError, setLocalError] = useState<string | null>(null);

    const initial = { file: null, kitId: 0, alleleFormat: null };

    const kits = useKits({ labId: null, defaultId: null });
    const alleleFormats = useAlleleFormats();

    const dlg = useDialogContext();

    const { parsed, loading, error, parseFile, reset, isUas } = useSampleParser();

    const handleFile = async (file?: File | null) => {
        setLocalError(null);
        if (!file) {
            setLocalError("No file selected");
            return;
        }
        try {
            const result = await parseFile(file, kits.selectedId ?? 0, alleleFormats.selectedId ?? undefined);
            if (onParsed) onParsed(result);
        } catch (err: any) {
            setLocalError(err?.message ?? "Failed to parse file");
        }
    };

    return (
        <>
            <Formik initialValues={initial} validationSchema={FileSchema}
                onSubmit={async (values, actions) => {
                    // console.log("Submitting form with values:", values);
                    actions.setSubmitting(true);
                    await handleFile(values.file ?? null);
                    actions.setSubmitting(false);
                }} enableReinitialize>
                {({ setFieldValue, isSubmitting, errors, touched, handleSubmit, values }) => (
                    <>
                        <SUIForm as={ModalContent}>
                            <SemanticFormikSelect
                                name="kitId"
                                label={"Loci Order"}
                                placeholder={"Select"}
                                required
                                {...kits.getDropdownProps()}
                            />
                            <SUIFormField>
                                <label>Sample file (CSV or TSV)</label>
                                <div style={{ border: "2px dashed #ccc", padding: "2rem", textAlign: "center", marginTop: "0.5rem" }}>
                                    <div>

                                        <input
                                            style={{ display: "none" }}
                                            id="file"
                                            type="file"
                                            // accept=".csv,.tsv,text/csv,text/tab-separated-values"
                                            onChange={(e) => {
                                                setLocalError(null);
                                                reset();
                                                const f = e.currentTarget.files?.[0] ?? null;
                                                setFieldValue("file", f);
                                            }}
                                        />
                                        <label htmlFor={'file'} style={{ cursor: "pointer", color: "#0084ff" }}>
                                            <>
                                                <Icon name="upload" size="big" color="grey" /><br />
                                                Click to browse files
                                            </>
                                        </label>
                                        {errors.file && touched.file ? (
                                            <Message negative content={String(errors.file)} />
                                        ) : null}
                                    </div>
                                    <div style={{ color: "#666", fontSize: "0.9em", marginTop: "0.5rem" }}>
                                        {values.file?.name || "Supported formats: CSV, TXT, XML, PNG"}
                                    </div>

                                </div>
                            </SUIFormField>

                            {isUas &&
                                <SemanticFormikSelect
                                    id="alleleFormat"
                                    name="alleleFormat"
                                    label={window.translation.sample.sampleInUAS}
                                    {...alleleFormats.getDropdownProps()}
                                />
                            }


                            {(error || localError) && (
                                <Message negative style={{ marginTop: 12 }}>
                                    <MessageHeader>Parsing error</MessageHeader>
                                    <p>{localError ?? error}</p>
                                </Message>
                            )}

                            {parsed && (
                                <div style={{ marginTop: 16 }}>
                                    <Message info>
                                        <MessageHeader>Parsed {parsed.rows.length} rows</MessageHeader>
                                        <p>Showing up to first 10 rows.</p>
                                    </Message>

                                    <div style={{ overflowX: "auto" }}>
                                        <Table celled compact selectable>
                                            <TableHeader>
                                                <TableRow>
                                                    {parsed.columns.map((col) => (
                                                        <TableHeaderCell key={col}>{col}</TableHeaderCell>
                                                    ))}
                                                </TableRow>
                                            </TableHeader>

                                            <TableBody>
                                                {parsed.rows.slice(0, 10).map((row, idx) => (
                                                    <TableRow key={idx}>
                                                        {parsed.columns.map((col) => (
                                                            <TableCell key={col + idx}>{row[col]}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}


                        </SUIForm>
                        <ModalActions>
                            <BPButton type="button" onClick={() => { dlg.hide(); }}>{window.translation.common.close ?? 'Close'}</BPButton>
                            <BPButton
                                onClick={() => handleSubmit()}
                                type="submit"
                                primary
                                loading={isSubmitting || loading}
                                disabled={isSubmitting || loading}
                                icon
                                labelPosition="left"
                            >
                                <Icon name="upload" />
                                {submitLabel}
                            </BPButton>
                        </ModalActions>
                    </>
                )}
            </Formik>

        </>
    );
}
